import { useQuery } from "@tanstack/react-query";
import { Clock, Loader2, UserX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUrlState } from "@/hooks/useUrlState";
import {
  listPendingDeletions,
  type AccountDeletionReason,
  type AdminPendingDeletion,
} from "@/services/adminApi";
import type { DeletionRequestsUrlState } from "@/types/pageState";

const PAGE_SIZE = 50;

const REASON_LABELS: Record<AccountDeletionReason, string> = {
  not_using: "Not using",
  too_expensive: "Too expensive",
  missing_features: "Missing features",
  privacy: "Privacy",
  found_alternative: "Found alternative",
  other: "Other",
};

const deletionKeys = {
  all: ["admin", "deletions"] as const,
  list: (page: number) => [...deletionKeys.all, "list", page] as const,
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatReason(reason: AccountDeletionReason | null): string {
  if (!reason) return "—";
  return REASON_LABELS[reason] ?? reason;
}

function daysUntil(iso: string, now = Date.now()): number {
  const due = new Date(iso).getTime();
  return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
}

function isOverdue(row: AdminPendingDeletion, now = Date.now()): boolean {
  return new Date(row.deletion_scheduled_for).getTime() <= now;
}

function DueBadge({ scheduledFor }: { scheduledFor: string }) {
  const days = daysUntil(scheduledFor);
  if (days <= 0) {
    return <Badge variant="destructive">Overdue</Badge>;
  }
  return (
    <Badge variant="outline">
      {days === 1 ? "1 day left" : `${days} days left`}
    </Badge>
  );
}

export default function DeletionRequests() {
  const [urlState, setUrlState] = useUrlState<DeletionRequestsUrlState>({
    schema: { page: "number" },
    defaults: { page: 1 },
    replace: true,
  });

  const page = urlState.page ?? 1;
  const offset = (page - 1) * PAGE_SIZE;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: deletionKeys.list(page),
    queryFn: () => listPendingDeletions({ limit: PAGE_SIZE, offset }),
  });

  const deletions = data?.deletions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const overdueCount = deletions.filter((row) => isOverdue(row)).length;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Deletion Requests</h2>
        <p className="text-sm text-muted-foreground">
          Accounts in the 30-day grace period. Purge still runs via the daily
          cron — this page is read-only.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          icon={<UserX className="size-4 text-muted-foreground" />}
          label="Pending"
          value={isLoading ? "…" : String(total)}
        />
        <MetricCard
          icon={<Clock className="size-4 text-muted-foreground" />}
          label="Overdue on this page"
          value={isLoading ? "…" : String(overdueCount)}
        />
      </div>

      {isError ? (
        <div className="text-sm text-destructive py-8">
          {error instanceof Error
            ? error.message
            : "Failed to load deletion requests."}
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="size-5 animate-spin" />
          Loading…
        </div>
      ) : deletions.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8">
          No pending deletion requests.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Scheduled purge</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deletions.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div className="font-medium text-sm">
                      {row.name ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {row.email ?? `#${row.id}`}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(row.deletion_requested_at)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(row.deletion_scheduled_for)}
                  </TableCell>
                  <TableCell>
                    <DueBadge scheduledFor={row.deletion_scheduled_for} />
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatReason(row.deletion_reason)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() =>
                setUrlState((prev) => ({ ...prev, page: page - 1 }))
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() =>
                setUrlState((prev) => ({ ...prev, page: page + 1 }))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function MetricCard({ icon, label, value }: MetricCardProps) {
  return (
    <Card>
      <CardHeader className="pb-1 pt-4 px-4">
        <CardTitle className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div
          className="text-2xl font-semibold truncate"
          aria-label={`${label}: ${value}`}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
